import { useSelector } from 'react-redux';
import PlayPodcast from '../components/GeneratedPodcast/PlayPodcast';
import PodcastDialogue from '../components/GeneratedPodcast/PodcastDialogue';
import PodcastHeader from '../components/PodcastEditor/PodcastHeader';
import { RootState } from 'store';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import PlayPodcastMobile from '../components/GeneratedPodcast/PlayPodcastMobile';
import { Button } from 'components/ui/button';
import DiscardPodcast from '../modals/DiscardPodcast';
import toast from 'react-hot-toast';
import { DialogueHighlightProvider, useDialogueHighlight } from '../hooks/useDialogueHighlight';
import useIsMobile from 'hooks/useIsMobile';

// Inner component to calculate and set dialogue timings
const DialogueTimingCalculator = ({ dialogues }: { dialogues: any[] }) => {
  const { setDialogueTimings } = useDialogueHighlight();
  const [isCalculating, setIsCalculating] = useState(true);
  const audioElements = useRef<HTMLAudioElement[]>([]);
  const loadedCount = useRef(0);
  
  useEffect(() => {
    if (!isCalculating || dialogues.length === 0) return;
    
    // Create default timings with fallback durations
    // This ensures we have some timings even if audio loading fails
    const createDefaultTimings = () => {
      const defaultDuration = 8; // seconds
      const timings = [];
      let currentTime = 0;
      
      for (let i = 0; i < dialogues.length; i++) {
        timings.push({
          start: currentTime,
          end: currentTime + defaultDuration
        });
        currentTime += defaultDuration;
      }
      
      return timings;
    };
    
    // Set initial fallback timings
    const fallbackTimings = createDefaultTimings();
    setDialogueTimings(fallbackTimings);
    
    // Create audio elements for each dialogue to get durations
    audioElements.current = dialogues.map(() => new Audio());
    
    // Set a timeout to ensure we don't block if audio loading is slow
    const timeoutId = setTimeout(() => {
      if (loadedCount.current < dialogues.length) {
        setIsCalculating(false);
      }
    }, 5000); // 5 second timeout
    
    // Set up event listeners for when audio metadata loads
    const handleMetadataLoaded = (index: number, duration: number) => {
      loadedCount.current += 1;
      
      // When all audio durations are loaded, calculate timings
      if (loadedCount.current === dialogues.length) {
        clearTimeout(timeoutId);
        
        const timings = [];
        let currentStart = 0;
        
        // Calculate start and end times for each dialogue
        for (let i = 0; i < dialogues.length; i++) {
          // Get duration (fallback to 8 seconds if it's zero or invalid)
          const audioDuration = audioElements.current[i].duration || 8;
          const duration = isNaN(audioDuration) || audioDuration <= 0 ? 8 : audioDuration;
          
          timings.push({
            start: currentStart,
            end: currentStart + duration
          });
          currentStart += duration;
        }
        
        // Set the dialogue timings in context
        setDialogueTimings(timings);
        setIsCalculating(false);
        
        // Clean up audio elements
        audioElements.current.forEach(audio => {
          audio.src = '';
          audio.load();
        });
      }
    };
    
    // Set up audio elements and load metadata
    dialogues.forEach((dialogue, index) => {
      const audio = audioElements.current[index];
      
      // Use preview_url if available
      if (dialogue.preview_url) {
        audio.addEventListener('loadedmetadata', () => {
          handleMetadataLoaded(index, audio.duration);
        });
        
        audio.addEventListener('error', () => {
          console.error(`Error loading audio for dialogue ${index}, using fallback duration`);
          handleMetadataLoaded(index, 8); // use 8 seconds as fallback
        });
        
        // Set crossOrigin to handle CORS issues
        audio.crossOrigin = "anonymous";
        audio.src = dialogue.preview_url;
        audio.load();
      } else {
        // If no preview URL, use fallback duration
        handleMetadataLoaded(index, 8); // use 8 seconds as fallback
      }
    });
    
    // Clean up function
    return () => {
      clearTimeout(timeoutId);
      
      audioElements.current.forEach(audio => {
        audio.src = '';
        audio.load();
      });
    };
  }, [dialogues, isCalculating, setDialogueTimings]);
  
  return null; // This is just a logic component, no rendering
};

const GeneratedPodcast = () => {
  const { dialogues } = useSelector(
    (state: RootState) => state.podcastRes.data
  );
  const { podcast_url } = useSelector(
    (state: RootState) => state.podcastPlayback
  );

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleDownload = () => {
    try {
      if (!podcast_url) {
        toast.error('No podcast available to download');
        return;
      }
      
      const a = document.createElement('a');
      a.href = podcast_url;
      a.download = 'Podcast.mp3';
      
      // Checking if URL is valid
      if (!podcast_url.startsWith('http')) {
        toast.error('Invalid podcast URL');
        return;
      }
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download podcast');
    }
  };

  useEffect(() => {
    if (dialogues.length === 0) {
      navigate('/ai-podcast-generator/get-started');
    }
  }, [dialogues, navigate]);

  if (dialogues.length === 0) return null;

  // Wrap content with our dialogue highlight provider
  return (
    <DialogueHighlightProvider dialoguesCount={dialogues.length}>
      <DialogueTimingCalculator dialogues={dialogues} />
      <div className="flex items-stretch gap-2 overflow-x-hidden">
        <div className="bg-background-main w-screen min-h-screen rounded-lg flex flex-col">
          {/* Header */}
          <div className="flex-none">
            <PodcastHeader isGenerated={true} />
          </div>

          {/* Dialogues */}
          <div className="flex-grow flex flex-col px-4 sm:px-0 max-w-[1200px] py-10 mx-auto gap-16 pb-[240px]">
            {dialogues.map((dialogue, index) => (
              <PodcastDialogue
                key={index}
                name={dialogue.character_name}
                dialogue={dialogue.dialogue}
                index={index}
              />
            ))}
          </div>

          {/* Play Area */}
          <div
            className={`absolute bottom-[70px] left-1/2 transform -translate-x-1/2 rounded-lg ${isMobile ? 'bg-[#168566] w-[340px]' : 'bg-[#168566] px-8 bottom-[24px] left-[55%] h-[128px] max-w-[1200px] w-[84%]'}`}
          >
            {isMobile ? (
              <PlayPodcastMobile url={podcast_url} />
            ) : (
              <PlayPodcast url={podcast_url} />
            )}
          </div>
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-main border-t flex gap-4 md:hidden">
            <DiscardPodcast />
            <Button
              variant="customPrimary"
              size="sm"
              className="text-[14px] font-normal sm:hidden w-full"
              onClick={handleDownload}
            >
              Download(.mp3)
            </Button>
          </div>
        </div>
      </div>
    </DialogueHighlightProvider>
  );
};

export default GeneratedPodcast;
